'use client';

import { useState, useMemo, useEffect } from 'react';

import { PlanCard } from '@/elements/plan-card/plan-card.component';
import { Container, HorizontalContainer } from './plans.elements';

import { useDeviceSize } from '@/hooks/use-device-size.hook';
import { breakpoints } from '@/styles/theme';
import { useAuth } from '@/providers/auth.provider';

const Plans = ({
  educationPlans = [],
  subscriptionPlans = [],
  education,
  registration,
  dynamic,
  horizontal,
  actionType,
}) => {
  const [width] = useDeviceSize();
  const { user } = useAuth();

  const plans = [
    ...subscriptionPlans.map((plan) => ({ ...plan, ...plan.plan })),
    ...educationPlans.map((plan) => ({
      ...plan,
      activeFrom: plan.educationDetails.activeFrom,
      activeTo: plan.educationDetails.activeTo,
    })),
  ];

  const [selectedPlan, setSelectedPlan] = useState(
    plans.length > 0 ? plans[Math.floor(plans.length / 2)].id : null
  );

  const usedFreePlan = plans.findIndex(
    ({ plan }) =>
      plan?.subscriptionDetails?.freePlan &&
      plan.subscriptionDetails.students.some((student) => student.id === user?.id)
  );

  if (user && subscriptionPlans.length > 0 && usedFreePlan > -1) {
    plans.splice(usedFreePlan, 1);
  }

  const slidesToScroll = useMemo(() => {
    if (width > breakpoints.abs.laptop) {
      return 3;
    } else if (width > breakpoints.abs.mobile) {
      return 2;
    } else {
      return 1;
    }
  }, [width]);

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
  };

  useEffect(() => {
    if (width < breakpoints.abs.mobile && plans.length > 0) {
      setSelectedPlan(plans[0].id);
    }
  }, [width]);

  if (!width) return null;

  if (horizontal) {
    return (
      <HorizontalContainer>
        {plans
          .filter((plan) => {
            // If an education plan with maxRegistrations set and reached, don't display the plan
            if (plan?.educationDetails?.maxRegistrations) {
              if (
                plan.educationDetails.maxRegistrations - plan.educationDetails.registrationCount ===
                0
              ) {
                return false;
              }
            }

            return true;
          })
          .filter((plan) => {
            const now = new Date();
            const { activeFrom, activeTo } = plan;

            // If both activeFrom and activeTo are present, check if the current date is between them
            if (activeFrom && activeTo) {
              return now >= new Date(activeFrom) && now <= new Date(activeTo);
            }
            // If only activeFrom is present, check if the current date is after the activeFrom date
            else if (activeFrom) {
              return now >= new Date(activeFrom);
            }
            // If only activeTo is present, check if the current date is before the activeTo date
            else if (activeTo) {
              return now <= new Date(activeTo);
            }
            // If neither activeFrom nor activeTo are present, the plan is always shown
            return true;
          })
          .map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              education={education}
              onClick={() => handleSelectPlan(plan.id)}
              selected={selectedPlan === plan.id || plans.length === 1}
              registration={registration}
              dynamic={dynamic}
              horizontal
              actionType={actionType}
            />
          ))}
      </HorizontalContainer>
    );
  }

  return (
    <Container
      height="600px"
      withControls={plans.length > slidesToScroll && width < breakpoints.abs.mobile}
      withIndicators={plans.length > slidesToScroll}
      emblaOptions={{ slidesToScroll }}
    >
      {plans
        .filter((plan) => {
          // If an education plan with maxRegistrations set and reached, don't display the plan
          if (plan?.educationDetails?.maxRegistrations) {
            if (
              plan.educationDetails.maxRegistrations - plan.educationDetails.registrationCount ===
              0
            ) {
              return false;
            }
          }

          return true;
        })
        .filter((plan) => {
          const now = new Date();
          const { activeFrom, activeTo } = plan;

          // If both activeFrom and activeTo are present, check if the current date is between them
          if (activeFrom && activeTo) {
            return now >= new Date(activeFrom) && now <= new Date(activeTo);
          }
          // If only activeFrom is present, check if the current date is after the activeFrom date
          else if (activeFrom) {
            return now >= new Date(activeFrom);
          }
          // If only activeTo is present, check if the current date is before the activeTo date
          else if (activeTo) {
            return now <= new Date(activeTo);
          }
          // If neither activeFrom nor activeTo are present, the plan is always shown
          return true;
        })
        .map((plan) => (
          <div key={plan.id} style={{ paddingTop: 10 }}>
            <PlanCard
              plan={plan}
              education={education}
              onClick={() => handleSelectPlan(plan.id)}
              selected={selectedPlan === plan.id || plans.length === 1}
              registration={registration}
              dynamic={dynamic}
            />
          </div>
        ))}
    </Container>
  );
};

export { Plans };
